import {
  AiFullResponse,
  AiIntent,
  AiQueryResponse,
  ScanConfiguration
} from "../../components/Pages/Diary/SpaceScanner/SpaceScanner.types";

const VALID_INTENTS: Set<AiIntent> = new Set([
  "configure",
  "adjust",
  "explain",
  "compare",
  "advise",
  "out_of_scope"
]);

const isValidIntent = (intent: unknown): intent is AiIntent =>
  typeof intent === "string" && VALID_INTENTS.has(intent as AiIntent);

export interface ParseOptimisationQueryOptions {
  venueId: string;
  query: string;
  baseUrl: string;
  token: string;
  currentConfig?: ScanConfiguration;
  date?: string;
  /** Optional scan results passed for the 'advise' intent when scan result context is needed */
  scanResults?: unknown[];
  /** When true, the backend runs optimisation immediately for configure intents */
  autoScan?: boolean;
}

const fetchAiQuery = async ({
  venueId,
  query,
  baseUrl,
  token,
  currentConfig,
  date,
  scanResults,
  autoScan
}: ParseOptimisationQueryOptions): Promise<Response> =>
  fetch(`${baseUrl}/venues/${venueId}/ai/parse-optimisation-query`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ query, currentConfig, date, scanResults, autoScan })
  });

const buildFallbackQueryResponse = (
  currentConfig?: ScanConfiguration
): AiQueryResponse => ({
  strategy: currentConfig?.optimizationStrategy ?? "balanced",
  allowOverCapacity: currentConfig?.allowOverCapacity ?? false,
  searchSeatedCapacity: currentConfig?.searchSeatedCapacity ?? false,
  explanation: "Could not parse your request. Using current settings."
});

const buildFallbackFullResponse = (
  currentConfig?: ScanConfiguration
): AiFullResponse => ({
  intent: "configure",
  ...buildFallbackQueryResponse(currentConfig)
});

/**
 * Calls the AI parse-optimization-query endpoint to interpret a natural
 * language query into scan configuration parameters.
 */
export const parseOptimizationQuery = async (
  options: ParseOptimisationQueryOptions
): Promise<AiQueryResponse> => {
  try {
    const response = await fetchAiQuery(options);

    if (!response.ok) {
      return buildFallbackQueryResponse(options.currentConfig);
    }

    const data = await response.json();
    return (data.payload ?? data) as AiQueryResponse;
  } catch {
    return buildFallbackQueryResponse(options.currentConfig);
  }
};

/**
 * Calls the AI parse-optimization-query endpoint and returns a typed
 * discriminated union response based on the detected intent.
 * Falls back to a legacy configure response for backward compatibility
 * when the response does not include an intent field.
 */
export const parseOptimizationQueryFull = async (
  options: ParseOptimisationQueryOptions
): Promise<AiFullResponse> => {
  try {
    const response = await fetchAiQuery(options);

    if (!response.ok) {
      return buildFallbackFullResponse(options.currentConfig);
    }

    const data = await response.json();
    const payload = data.payload ?? data;

    if (isValidIntent(payload.intent)) {
      return payload as AiFullResponse;
    }

    return {
      intent: "configure",
      strategy:
        payload.strategy ??
        options.currentConfig?.optimizationStrategy ??
        "balanced",
      allowOverCapacity:
        payload.allowOverCapacity ??
        options.currentConfig?.allowOverCapacity ??
        false,
      searchSeatedCapacity:
        payload.searchSeatedCapacity ??
        options.currentConfig?.searchSeatedCapacity ??
        false,
      explanation: payload.explanation ?? "Configuration updated."
    };
  } catch {
    return buildFallbackFullResponse(options.currentConfig);
  }
};
